/**
 * ContactModal
 * ============
 * In-page contact form — replaces both the Messenger redirect (which hit a
 * Facebook login wall on desktop) and the mailto: link (which made clients
 * copy/paste an address into an email app).
 *
 * Opens from ANYWHERE on the site via a custom window event:
 *
 *   window.dispatchEvent(new CustomEvent("open-contact-modal", {
 *     detail: { source: "topbar" }   // optional — appears in subject line
 *   }))
 *
 * Mount once at the root of <App />. POSTs to /api/contact/quick-message
 * which emails bookings + Karine + Marc with reply-to set to the client.
 */
import React, { useEffect, useState, useCallback } from "react";
import { createPortal } from "react-dom";
import axios from "axios";
import { toast } from "sonner";
import { X, Send, Loader2, MessageCircle, CheckCircle2, CalendarClock } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
// "Please reschedule me" requests kept landing in the inbox (Oct 10 2026 ·
// Karine). When the message reads like one, surface the one-tap self-serve
// reschedule link right under the textarea.
const RESCHEDULE_INTENT = /re-?sched|re-?book|move (my|the|our) app|change (my|the|our) (app|date|time)|different (day|date|time)|another (day|date|time)|push (it|my|the)|reprogram|déplacer|changer (ma|mon|la) (date|rendez)/i;

export default function ContactModal() {
  const [open, setOpen] = useState(false);
  const [source, setSource] = useState("website");
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [message, setMessage] = useState("");
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);

  // Reset state whenever modal closes
  const close = useCallback(() => {
    setOpen(false);
    setTimeout(() => {
      setSent(false);
      setSending(false);
    }, 250);
  }, []);

  // Pre-fill from saved client profile if we have one (booking flow stores
  // these on completed bookings so returning clients don't re-type info)
  useEffect(() => {
    if (!open) return;
    try {
      const raw = localStorage.getItem("kl_last_client") || localStorage.getItem("kl_client_profile");
      if (raw) {
        const p = JSON.parse(raw);
        if (p?.name && !name) setName(p.name);
        if (p?.email && !email) setEmail(p.email);
        if (p?.phone && !phone) setPhone(p.phone);
      }
    } catch {
      /* ignore */
    }
  }, [open]);

  // Listen for the global open event from anywhere on the site
  useEffect(() => {
    const handler = (e) => {
      setSource(e?.detail?.source || "website");
      setOpen(true);
    };
    window.addEventListener("open-contact-modal", handler);
    return () => window.removeEventListener("open-contact-modal", handler);
  }, []);

  // GLOBAL INTERCEPTOR
  // -----------------------------------------------------------------
  // Catches any click on a legacy Messenger link (m.me/...) or a mailto
  // pointing at our inboxes and redirects it into this modal. This lets
  // us migrate the 18+ existing CTA links across the site without
  // touching each file. Real outbound links (to non-Karine domains,
  // tel:, etc.) are untouched.
  useEffect(() => {
    const isOurContactLink = (href) => {
      if (!href) return false;
      const h = href.toLowerCase();
      return (
        h.includes("m.me/precisernint") ||
        h.startsWith("mailto:bookings@karinelauzon.com") ||
        h.startsWith("mailto:info@karinelauzon.com") ||
        h.startsWith("mailto:karine@karinelauzon.com") ||
        h.startsWith("mailto:hello@karinelauzon.com") ||
        h.startsWith("mailto:contact@karinelauzon.com")
      );
    };

    const interceptor = (e) => {
      // Walk up to find the nearest <a> ancestor
      let node = e.target;
      while (node && node !== document) {
        if (node.tagName === "A" && isOurContactLink(node.getAttribute("href"))) {
          e.preventDefault();
          e.stopPropagation();
          // Best-effort source attribution from the link's testid/aria-label
          const src =
            node.getAttribute("data-testid") ||
            node.getAttribute("aria-label") ||
            "intercepted-link";
          setSource(src);
          setOpen(true);
          return;
        }
        node = node.parentNode;
      }
    };

    // Use capture so we run before any inline handlers / target=_blank
    document.addEventListener("click", interceptor, true);
    return () => document.removeEventListener("click", interceptor, true);
  }, []);

  // ESC to close
  useEffect(() => {
    if (!open) return;
    const onKey = (e) => {
      if (e.key === "Escape") close();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, close]);

  const submit = async (e) => {
    e?.preventDefault?.();
    if (sending) return;
    const cleanMsg = message.trim();
    if (!name.trim() || !email.trim() || !cleanMsg) {
      toast.error("Name, email and message are required.");
      return;
    }
    setSending(true);
    try {
      const res = await axios.post(`${API}/contact/quick-message`, {
        name: name.trim(),
        email: email.trim(),
        phone: phone.trim() || null,
        message: cleanMsg,
        source,
      });
      if (res?.data?.ok) {
        setSent(true);
        toast.success("Message sent — we'll reply shortly.");
        setName("");
        setPhone("");
        setMessage("");
      } else {
        toast.success(res?.data?.message || "Message saved — we'll be in touch.");
        setSent(true);
      }
    } catch (err) {
      toast.error("Couldn't send right now. Please try again or call (613) 850-0805.");
      console.error("[ContactModal] submit failed", err);
    } finally {
      setSending(false);
    }
  };

  // Lock body scroll while open — also prevents an iOS Safari quirk
  // where stacked fixed overlays (Welcome popup, sticky banners) capture
  // touches that should reach the modal. We do this in addition to the
  // portal mount below.
  useEffect(() => {
    if (!open) return;
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => { document.body.style.overflow = prevOverflow; };
  }, [open]);

  if (!open) return null;

  // Render the modal in a portal directly under <body>. This escapes
  // every parent stacking context (App wrapper transforms, BrowserRouter
  // suspense boundaries, BackToMain z-50, etc.) so the modal is
  // guaranteed to sit on top of everything else AND receive touches on
  // iOS Safari — where stacking-context bugs would otherwise let
  // invisible overlays steal taps.
  return createPortal(
    <div
      className="fixed inset-0 z-[2147483000] flex items-end sm:items-center justify-center"
      data-testid="contact-modal"
      onClick={close}
      style={{ touchAction: "manipulation" }}
    >
      {/* Backdrop */}
      <div className="absolute inset-0 bg-black/70 backdrop-blur-sm pointer-events-none" />

      {/* Card */}
      <div
        className="relative w-full sm:max-w-md mx-0 sm:mx-4 bg-[#1a1410] border border-amber-700/30 rounded-t-2xl sm:rounded-2xl shadow-2xl text-amber-50 overflow-hidden pointer-events-auto"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-start justify-between px-5 py-4 bg-gradient-to-r from-amber-900/50 to-amber-800/30 border-b border-amber-700/30">
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 rounded-full bg-amber-600/20 flex items-center justify-center">
              <MessageCircle className="w-5 h-5 text-amber-300" />
            </div>
            <div>
              <div className="text-base font-semibold text-amber-100" data-testid="contact-modal-title">
                Message Karine Lauzon RN
              </div>
              <div className="text-[11px] text-amber-300/70 mt-0.5">
                Typical reply within 1 hour during clinic hours
              </div>
            </div>
          </div>
          <button
            type="button"
            onClick={close}
            className="text-amber-300/70 hover:text-amber-100 transition-colors p-1 -mr-1"
            data-testid="contact-modal-close"
            aria-label="Close"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Body */}
        {sent ? (
          <div className="px-6 py-10 text-center" data-testid="contact-modal-success">
            <div className="w-14 h-14 mx-auto rounded-full bg-emerald-500/15 flex items-center justify-center mb-4">
              <CheckCircle2 className="w-8 h-8 text-emerald-400" />
            </div>
            <div className="text-lg font-semibold text-amber-50 mb-2">Message received ✨</div>
            <div className="text-sm text-amber-200/80 leading-relaxed">
              Thank you, {email || "there"}. We&apos;ll reply within an hour during clinic hours
              (Tue–Sat 9–5) — usually much sooner.
            </div>
            <button
              type="button"
              onClick={close}
              className="mt-6 px-6 py-2.5 rounded-full bg-amber-600 hover:bg-amber-500 text-white text-sm font-medium transition-colors"
              data-testid="contact-modal-done"
            >
              Done
            </button>
          </div>
        ) : (
          <form onSubmit={submit} className="px-5 py-5 space-y-3" data-testid="contact-modal-form">
            <div>
              <label className="block text-xs font-medium text-amber-300/80 mb-1.5">
                Your name <span className="text-amber-500">*</span>
              </label>
              <input
                type="text"
                value={name}
                onChange={(e) => setName(e.target.value)}
                required
                autoFocus
                placeholder="Jane Doe"
                className="w-full px-3.5 py-2.5 bg-black/40 border border-amber-700/40 rounded-lg text-amber-50 placeholder-amber-200/30 focus:outline-none focus:border-amber-500 focus:ring-1 focus:ring-amber-500/50 text-sm"
                data-testid="contact-modal-name"
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-amber-300/80 mb-1.5">
                Email <span className="text-amber-500">*</span>
              </label>
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                inputMode="email"
                placeholder="you@example.com"
                className="w-full px-3.5 py-2.5 bg-black/40 border border-amber-700/40 rounded-lg text-amber-50 placeholder-amber-200/30 focus:outline-none focus:border-amber-500 focus:ring-1 focus:ring-amber-500/50 text-sm"
                data-testid="contact-modal-email"
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-amber-300/80 mb-1.5">
                Phone <span className="text-amber-400/50 text-[10px]">(optional)</span>
              </label>
              <input
                type="tel"
                value={phone}
                onChange={(e) => setPhone(e.target.value)}
                inputMode="tel"
                placeholder="(613) 555-0123"
                className="w-full px-3.5 py-2.5 bg-black/40 border border-amber-700/40 rounded-lg text-amber-50 placeholder-amber-200/30 focus:outline-none focus:border-amber-500 focus:ring-1 focus:ring-amber-500/50 text-sm"
                data-testid="contact-modal-phone"
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-amber-300/80 mb-1.5">
                Your message <span className="text-amber-500">*</span>
              </label>
              <textarea
                value={message}
                onChange={(e) => setMessage(e.target.value)}
                required
                rows={4}
                placeholder="Hi! I'd love to ask about…"
                className="w-full px-3.5 py-2.5 bg-black/40 border border-amber-700/40 rounded-lg text-amber-50 placeholder-amber-200/30 focus:outline-none focus:border-amber-500 focus:ring-1 focus:ring-amber-500/50 text-sm resize-none"
                data-testid="contact-modal-message"
              />
              {RESCHEDULE_INTENT.test(message) && (
                <a
                  href="/my-appointments?intent=reschedule"
                  onClick={() => setOpen(false)}
                  className="mt-2 flex items-center gap-3 rounded-xl border-2 border-amber-400 bg-amber-500 hover:bg-amber-400 text-black px-4 py-3 no-underline shadow-lg"
                  data-testid="contact-reschedule-shortcut"
                >
                  <CalendarClock className="w-6 h-6 shrink-0" />
                  <span className="min-w-0">
                    <span className="block text-sm font-extrabold leading-tight">Reschedule it yourself in one tap</span>
                    <span className="block text-[11px] font-medium opacity-80 leading-tight">Sign in to My Account → pick a new day & time → done. No waiting for a reply.</span>
                  </span>
                </a>
              )}
            </div>
            <button
              type="submit"
              disabled={sending}
              className="w-full mt-2 px-4 py-3 bg-gradient-to-r from-amber-600 to-amber-700 hover:from-amber-500 hover:to-amber-600 disabled:opacity-50 disabled:cursor-not-allowed text-white rounded-lg font-medium text-sm flex items-center justify-center gap-2 transition-all"
              data-testid="contact-modal-submit"
            >
              {sending ? (
                <>
                  <Loader2 className="w-4 h-4 animate-spin" />
                  Sending…
                </>
              ) : (
                <>
                  <Send className="w-4 h-4" />
                  Send message
                </>
              )}
            </button>
            <p className="text-[10px] text-amber-300/50 text-center pt-1">
              By sending you agree we may reply by email or phone. We never share your info.
            </p>
          </form>
        )}
      </div>
    </div>,
    document.body
  );
}

/**
 * Helper to open the contact modal from anywhere.
 *   openContactModal({ source: "topbar" })
 */
export function openContactModal(detail = {}) {
  try {
    window.dispatchEvent(new CustomEvent("open-contact-modal", { detail }));
  } catch {
    // SSR or unsupported env — no-op
  }
}
